/**
 * SPCB Document Type - v1.17.0
 *
 * Enfoque simplificado usando la API oficial de WooCommerce:
 * 1. PHP registra el campo via woocommerce_register_additional_checkout_field()
 * 2. CSS `order` property posiciona el campo correctamente en el contenedor flexbox
 * 3. Este script transforma el select en radio cards (mejora visual)
 * 4. Fetch interceptor asegura que el valor se envíe correctamente
 * 5. v1.16.22: Fetch interceptor también inyecta is_local_pickup para fix de race condition
 * 6. v1.16.25: Fetch interceptor inyecta contact fields en billing/shipping_address
 *              para fix de race condition con nombres vacíos
 * 7. v1.17.0: Fetch interceptor inyecta is_branch_to_agency y branch_reference
 *             para el nuevo checkbox inline de envío a sucursal
 *
 * @package SuperPymeCheckoutBlocks
 * @since 1.17.0
 */

const DEBUG = window.location.search.includes('spcb_debug=1');
const log = (...args) => DEBUG && console.log('[SPCB DocType]', ...args);

// SVG Icons for radio cards
const icons = {
    boleta: `<svg class="spcb-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
        <polyline points="14 2 14 8 20 8"/>
        <line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/>
    </svg>`,
    factura: `<svg class="spcb-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
        <polyline points="14 2 14 8 20 8"/>
        <path d="M9 15l2 2 4-4"/>
    </svg>`
};

// Descriptions for radio cards
const descriptions = {
    boleta: 'Para personas naturales',
    factura: 'Para empresas con RUT'
};

/**
 * Transform WooCommerce select into radio cards
 * The select is rendered by WooCommerce via Additional Checkout Fields API
 * We transform it visually while keeping the select in the DOM for form data
 */
function transformSelectToRadioCards(select) {
    if (!select || select.dataset.spcbTransformed === 'true') {
        return false;
    }

    const wrapper = select.closest('.wc-block-components-select-input');
    if (!wrapper) {
        log('Select wrapper not found');
        return false;
    }

    // Get options from the select
    const options = Array.from(select.options)
        .filter(opt => opt.value && opt.value !== '')
        .map(opt => ({
            value: opt.value,
            label: opt.textContent || opt.value,
            selected: opt.selected || opt.value === (window.spcbPreferences?.document_type || 'boleta')
        }));

    if (options.length === 0) {
        log('No options found in select');
        return false;
    }

    // Create radio cards container
    const radioContainer = document.createElement('div');
    radioContainer.className = 'spcb-radio-cards';
    radioContainer.setAttribute('role', 'radiogroup');
    radioContainer.setAttribute('aria-label', 'Tipo de documento');

    options.forEach(opt => {
        const card = document.createElement('div');
        card.className = 'spcb-radio-card';

        const inputId = `spcb-doc-type-${opt.value}`;
        card.innerHTML = `
            <input type="radio" name="spcb-document-type-radio" id="${inputId}"
                   value="${opt.value}" ${opt.selected ? 'checked' : ''}>
            <label for="${inputId}">
                ${icons[opt.value] || ''}
                <span class="spcb-label-text">${opt.label}</span>
                <span class="spcb-label-desc">${descriptions[opt.value] || ''}</span>
            </label>
        `;

        const radio = card.querySelector('input');

        // Roving tabindex: only selected radio has tabindex="0"
        radio.setAttribute('tabindex', opt.selected ? '0' : '-1');

        radio.addEventListener('change', function() {
            if (this.checked) {
                // Update roving tabindex
                const allRadios = radioContainer.querySelectorAll('input[type="radio"]');
                allRadios.forEach(r => r.setAttribute('tabindex', r === this ? '0' : '-1'));

                handleDocumentTypeChange(select, this.value);
            }
        });

        radioContainer.appendChild(card);
    });

    // Keyboard navigation for radiogroup (roving tabindex pattern)
    radioContainer.addEventListener('keydown', function(e) {
        if (!['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(e.key)) {
            return;
        }

        e.preventDefault();

        const radios = Array.from(this.querySelectorAll('input[type="radio"]'));
        const currentIndex = radios.findIndex(r => r === document.activeElement);

        if (currentIndex === -1) return;

        // Calculate next index (wrap around)
        let nextIndex;
        if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
            nextIndex = (currentIndex + 1) % radios.length;
        } else {
            nextIndex = (currentIndex - 1 + radios.length) % radios.length;
        }

        // Update tabindex (roving)
        radios.forEach((r, i) => {
            r.setAttribute('tabindex', i === nextIndex ? '0' : '-1');
        });

        // Focus and select
        radios[nextIndex].focus();
        radios[nextIndex].checked = true;
        radios[nextIndex].dispatchEvent(new Event('change', { bubbles: true }));
    });

    // Hide original select but keep in DOM for WooCommerce form data
    select.style.cssText = 'position:absolute;opacity:0;height:0;width:0;overflow:hidden;pointer-events:none;';
    select.setAttribute('tabindex', '-1');
    select.setAttribute('aria-hidden', 'true');

    // Hide WC UI elements
    const selectLabel = wrapper.querySelector('label:not(.spcb-radio-label)');
    if (selectLabel) {
        selectLabel.style.display = 'none';
    }
    const expandIcon = wrapper.querySelector('.wc-blocks-components-select__expand');
    if (expandIcon) {
        expandIcon.style.display = 'none';
    }

    // Insert radio cards before the hidden select
    select.parentNode.insertBefore(radioContainer, select);

    // Mark as transformed
    select.dataset.spcbTransformed = 'true';
    wrapper.classList.add('spcb-document-type-wrapper');

    // Set initial state
    const initialValue = select.value || window.spcbPreferences?.document_type || 'boleta';
    setDocumentTypeState(initialValue);

    log('Select transformed to radio cards, initial value:', initialValue);
    return true;
}

/**
 * Handle document type change
 * Updates all state sources to ensure consistency
 */
function handleDocumentTypeChange(select, value) {
    log('Document type changed to:', value);

    // 1. Update hidden select (WooCommerce form data)
    select.value = value;

    // 2. Dispatch change event for WooCommerce (with checkout lock guard)
    if (!window.spcbCheckoutLock?.isProcessing?.()) {
        select.dispatchEvent(new Event('change', { bubbles: true }));
    }

    // 3. Set all state
    setDocumentTypeState(value);

    // 4. Toggle invoice fields via existing function
    if (window.spcbDocument?.toggleInvoiceFields) {
        window.spcbDocument.toggleInvoiceFields(value === 'factura');
    }

    // 5. Update RUT help text if available
    const billingRutInput = document.querySelector('input[data-spcb-field="billing_rut"]');
    if (billingRutInput && window.spcbUpdateRutHelpText) {
        window.spcbUpdateRutHelpText(billingRutInput, 'billing_rut', value);
    }
}

/**
 * Set document type state in all storage locations
 */
function setDocumentTypeState(value) {
    // 1. Global state for fetch interceptor
    window.spcbDocumentType = value;

    // 2. Body class for CSS conditional styling
    document.body.classList.remove('spcb-boleta-mode', 'spcb-factura-mode');
    document.body.classList.add(`spcb-${value}-mode`);

    // 3. Cookie as backup for backend
    try {
        document.cookie = `spcb_document_type=${value}; path=/; SameSite=Lax`;
    } catch (e) {
        log('Cookie storage failed:', e.message);
    }

    // 4. Dispatch custom event for other scripts
    window.dispatchEvent(new CustomEvent('spcb:documentTypeChange', {
        detail: { documentType: value }
    }));
}

/**
 * Install fetch interceptor to inject document_type and is_local_pickup into checkout requests
 * This ensures the value is always sent correctly, even if WooCommerce React state is stale
 *
 * v1.16.22: Added is_local_pickup injection to fix race condition with Local Pickup
 * The DOM state (what the user sees) is the source of truth, not React internal state
 */
function installFetchInterceptor() {
    // Don't install twice
    if (window.spcbFetchInterceptorInstalled) {
        log('Fetch interceptor already installed');
        return;
    }

    const originalFetch = window.fetch;

    window.fetch = async function(url, options = {}) {
        // Only intercept checkout requests
        if (typeof url === 'string' && url.includes('/wc/store/v1/checkout')) {
            try {
                let body = null;
                let shouldStringifyBody = false;

                if (options.body && typeof options.body === 'string') {
                    body = JSON.parse(options.body);
                    shouldStringifyBody = true;
                } else if (options.body instanceof FormData) {
                    body = {};
                    options.body.forEach((value, key) => {
                        body[key] = value;
                    });
                    shouldStringifyBody = true;
                } else if (options.body && Object.prototype.toString.call(options.body) === '[object Object]') {
                    body = { ...options.body };
                    shouldStringifyBody = true;
                }

                if (body) {

                    // Ensure extensions.spcb exists
                    if (!body.extensions) {
                        body.extensions = {};
                    }
                    if (!body.extensions.spcb) {
                        body.extensions.spcb = {};
                    }

                    // v1.17.10: Get document_type from DOM FIRST (source of truth)
                    // This fixes bug where window.spcbDocumentType gets stale after modal changes
                    // Priority: 1. Radio checked (what user sees) 2. Select value 3. Global var 4. Default
                    const currentValue =
                        document.querySelector('input[name="spcb-document-type-radio"]:checked')?.value ||
                        document.querySelector('select[data-spcb-field="document_type"]')?.value ||
                        window.spcbDocumentType ||
                        'boleta';

                    body.extensions.spcb.document_type = currentValue;
                    log('Injected document_type into checkout request:', currentValue);

                    // v1.16.22: Inject is_local_pickup based on DOM state
                    // This fixes race condition where React state might be inconsistent
                    // We check the DOM directly because that's what the user actually sees/selected
                    const pickupRadio = document.querySelector(
                        '.wc-block-components-shipping-rates-control input[type="radio"][value*="pickup"]:checked, ' +
                        '.wc-block-components-shipping-rates-control input[type="radio"][value*="local_pickup"]:checked'
                    );
                    const isLocalPickup = !!pickupRadio;
                    body.extensions.spcb.is_local_pickup = isLocalPickup;
                    log('Injected is_local_pickup into checkout request:', isLocalPickup);

                    // v1.17.0: Inject is_branch_to_agency based on checkbox state
                    // This tells backend whether user wants delivery to branch office
                    const branchCheckbox = document.querySelector('#spcb-branch-checkbox:checked');
                    const isBranchToAgency = !!branchCheckbox;
                    body.extensions.spcb.is_branch_to_agency = isBranchToAgency;
                    log('Injected is_branch_to_agency into checkout request:', isBranchToAgency);

                    // v1.17.13: ROBUST branch_reference reading with multiple fallbacks
                    // Search order: visible inline field → hidden inline field → modal field → localStorage
                    let branchReference = '';

                    // Helper to check if element is visible (not display:none)
                    const isVisible = (el) => {
                        if (!el) return false;
                        const style = window.getComputedStyle(el);
                        return style.display !== 'none' && style.visibility !== 'hidden';
                    };

                    // Try multiple selectors for the inline field
                    const inlineSelectors = [
                        '[data-spcb-branch-reference="inline"]',
                        '#spcb-branch-reference-inline',
                        '.spcb-reference-field-container input.spcb-reference-input'
                    ];

                    // First pass: look for VISIBLE inline field
                    for (const selector of inlineSelectors) {
                        const el = document.querySelector(selector);
                        if (el && isVisible(el.closest('.spcb-reference-field-container') || el)) {
                            branchReference = el.value || '';
                            if (branchReference) {
                                log('Found branch_reference from visible inline field:', selector, branchReference);
                                break;
                            }
                        }
                    }

                    // Second pass: look for any inline field (even if hidden)
                    if (!branchReference) {
                        for (const selector of inlineSelectors) {
                            const el = document.querySelector(selector);
                            if (el && el.value) {
                                branchReference = el.value;
                                log('Found branch_reference from hidden inline field:', selector, branchReference);
                                break;
                            }
                        }
                    }

                    // Third: try modal field
                    if (!branchReference) {
                        const modalEl = document.querySelector('[data-spcb-branch-reference="modal"]');
                        if (modalEl && modalEl.value) {
                            branchReference = modalEl.value;
                            log('Found branch_reference from modal field:', branchReference);
                        }
                    }

                    // Fourth: fallback to localStorage
                    if (!branchReference) {
                        try {
                            branchReference = localStorage.getItem('spcb_branch_reference') || '';
                            if (branchReference) {
                                log('Found branch_reference from localStorage:', branchReference);
                            }
                        } catch (e) {}
                    }

                    // Always send it - backend will ignore if not branch shipping
                    body.extensions.spcb.branch_reference = branchReference;
                    log('Final branch_reference for checkout:', branchReference || '(empty)');

                    if (isBranchToAgency) {

                        // v1.17.0: Fill shipping_address.address_1 with placeholder when in branch mode
                        // This prevents WooCommerce validation error for empty address_1
                        // Backend skips number validation for branch mode, so no need for "1"
                        if (!body.shipping_address) body.shipping_address = {};
                        const currentAddress = body.shipping_address.address_1 || '';
                        // Only fill if empty OR if it's our placeholder (to update with reference if provided)
                        if (!currentAddress.trim() || currentAddress === 'Envío a Sucursal') {
                            // Use reference street if provided, otherwise use placeholder
                            const placeholder = branchReference || 'Envío a Sucursal';
                            body.shipping_address.address_1 = placeholder;
                            log('Filled shipping_address.address_1 for branch mode:', body.shipping_address.address_1);
                        }
                    }

                    // v1.16.25: Inject contact/invoice field values into billing_address and shipping_address
                    // This fixes race condition where WC Blocks sends empty billing data
                    // because the native billing fields are hidden and custom fields are used
                    // Pattern: Read from DOM (source of truth) → inject into JSON body (no events, no observers)
                    try {
                        // Read contact values from DOM (source of truth)
                        const contactFirstName = document.querySelector('#contact-spcb-contact_first_name')?.value ||
                            document.querySelector('input[data-spcb-field="contact_first_name"]')?.value || '';
                        const contactLastName = document.querySelector('#contact-spcb-contact_last_name')?.value ||
                            document.querySelector('input[data-spcb-field="contact_last_name"]')?.value || '';
                        const contactPhone = document.querySelector('#contact-spcb-contact_phone')?.value ||
                            document.querySelector('input[data-spcb-field="contact_phone"]')?.value || '';

                        log('Contact values from DOM:', { contactFirstName, contactLastName, contactPhone });

                        // Ensure address objects exist
                        if (!body.billing_address) body.billing_address = {};
                        if (!body.shipping_address) body.shipping_address = {};

                        // ALWAYS: Names go to both billing and shipping
                        if (contactFirstName) {
                            body.billing_address.first_name = contactFirstName;
                            body.shipping_address.first_name = contactFirstName;
                        }
                        if (contactLastName) {
                            body.billing_address.last_name = contactLastName;
                            body.shipping_address.last_name = contactLastName;
                        }

                        // ALWAYS: contact_phone goes to shipping_phone
                        if (contactPhone) {
                            body.shipping_address.phone = contactPhone;
                        }

                        // Check document type for billing phone and invoice fields
                        const isFactura = currentValue === 'factura';

                        if (isFactura) {
                            // FACTURA: Read invoice fields and map to native billing fields
                            const invoiceCompany = document.querySelector('#contact-spcb-invoice_company')?.value ||
                                document.querySelector('input[data-spcb-field="invoice_company"]')?.value || '';
                            const invoiceAddress = document.querySelector('#contact-spcb-invoice_address')?.value ||
                                document.querySelector('input[data-spcb-field="invoice_address"]')?.value || '';
                            const invoiceRegion = document.querySelector('#contact-spcb-invoice_region')?.value ||
                                document.querySelector('select[data-spcb-field="invoice_region"]')?.value || '';
                            const invoiceComuna = document.querySelector('#contact-spcb-invoice_comuna')?.value ||
                                document.querySelector('select[data-spcb-field="invoice_comuna"]')?.value || '';
                            const invoicePhone = document.querySelector('#contact-spcb-invoice_phone')?.value ||
                                document.querySelector('input[data-spcb-field="invoice_phone"]')?.value || '';

                            log('Invoice values from DOM:', { invoiceCompany, invoiceAddress, invoiceRegion, invoiceComuna, invoicePhone });

                            // Map invoice fields to native billing fields
                            if (invoiceCompany) {
                                body.billing_address.company = invoiceCompany;
                            }
                            if (invoiceAddress) {
                                body.billing_address.address_1 = invoiceAddress;
                            }
                            if (invoiceRegion) {
                                body.billing_address.state = invoiceRegion;
                            }
                            if (invoiceComuna) {
                                body.billing_address.city = invoiceComuna;
                            }
                            if (invoicePhone) {
                                body.billing_address.phone = invoicePhone;
                            }

                            // Store invoice fields in extensions.spcb for backend recovery
                            body.extensions.spcb.invoice_company = invoiceCompany;
                            body.extensions.spcb.invoice_address = invoiceAddress;
                            body.extensions.spcb.invoice_region = invoiceRegion;
                            body.extensions.spcb.invoice_comuna = invoiceComuna;
                            body.extensions.spcb.invoice_phone = invoicePhone;

                            log('Injected invoice values into billing_address');
                        } else {
                            // BOLETA: contact_phone also goes to billing_phone
                            if (contactPhone) {
                                body.billing_address.phone = contactPhone;
                            }
                        }

                        // Store contact fields in extensions.spcb for backend recovery
                        body.extensions.spcb.contact_first_name = contactFirstName;
                        body.extensions.spcb.contact_last_name = contactLastName;
                        body.extensions.spcb.contact_phone = contactPhone;

                        log('Injected contact values into billing/shipping_address');
                    } catch (contactError) {
                        log('Error injecting contact values:', contactError.message);
                    }

                    if (shouldStringifyBody) {
                        options.body = JSON.stringify(body);
                        if (!options.headers) {
                            options.headers = { 'Content-Type': 'application/json' };
                        } else if (options.headers instanceof Headers) {
                            if (!options.headers.has('Content-Type')) {
                                options.headers.set('Content-Type', 'application/json');
                            }
                        } else if (typeof options.headers === 'object') {
                            if (!options.headers['Content-Type'] && !options.headers['content-type']) {
                                options.headers['Content-Type'] = 'application/json';
                            }
                        }
                    }
                } else {
                    log('Checkout request body is not JSON/string; skipping extension injection');
                }
            } catch (e) {
                log('Fetch interceptor error:', e.message);
            }
        }

        return originalFetch.call(this, url, options);
    };

    window.spcbFetchInterceptorInstalled = true;
    log('Fetch interceptor installed (v1.17.0 with is_local_pickup + contact fields + branch_to_agency)');
}

/**
 * Try to transform the document type select
 * Returns true if successful
 */
function tryTransform() {
    // Already transformed?
    if (document.querySelector('.spcb-radio-cards')) {
        return true;
    }

    // Find the select
    const select = document.querySelector('select[data-spcb-field="document_type"]');
    if (select) {
        return transformSelectToRadioCards(select);
    }

    return false;
}

/**
 * Initialize document type functionality
 */
function init() {
    log('Initializing document type (v1.17.0 - CSS order + pickup fix + contact fields + branch checkbox)');

    // Install fetch interceptor immediately
    installFetchInterceptor();

    // Try to transform immediately
    if (tryTransform()) {
        return;
    }

    // Retry with increasing delays
    const delays = [100, 300, 500, 1000, 2000];
    delays.forEach(delay => {
        setTimeout(() => {
            if (!document.querySelector('.spcb-radio-cards')) {
                tryTransform();
            }
        }, delay);
    });

    // Also observe for dynamic additions (React re-renders)
    const observer = new MutationObserver((mutations) => {
        if (document.querySelector('.spcb-radio-cards')) {
            return; // Already transformed
        }
        for (const mutation of mutations) {
            for (const node of mutation.addedNodes) {
                if (node.nodeType === Node.ELEMENT_NODE) {
                    if (node.matches?.('[data-spcb-field="document_type"]') ||
                        node.querySelector?.('[data-spcb-field="document_type"]')) {
                        tryTransform();
                        return;
                    }
                }
            }
        }
    });

    const checkout = document.querySelector('.wc-block-checkout, .wp-block-woocommerce-checkout');
    if (checkout) {
        observer.observe(checkout, { childList: true, subtree: true });
    }
}

// Start initialization
if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
} else {
    init();
}

// Expose for debugging
window.spcbDocumentTypeInit = init;
